import React, { createContext, useContext, useMemo, ReactNode, useEffect, useCallback } from 'react';
import { Source, ImportSyncSettingsInput } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import { useGetClientConfig } from '@frontend/applications/ProductFulfillmentApp/hooks/useGetClientConfig';
import { useUpdateImportSyncSettings } from '@frontend/applications/ProductFulfillmentApp/hooks/useUpdateImportSyncSettings';
import { useGetImportedCollections } from '../Products/hooks/useGetImportedCollections';

export type ImportedCollectionType = 'SHOPIFY' | 'CSV' | null;

interface ImportedCollectionTypeContextValue {
  importedCollectionType: ImportedCollectionType;
  loading: boolean;
}

// createOrUpdateImportSyncSettings

const ImportedCollectionTypeContext = createContext<ImportedCollectionTypeContextValue | undefined>(undefined);

interface ImportedCollectionTypeProviderProps {
  children: ReactNode;
}

export const ImportedCollectionTypeProvider: React.FC<ImportedCollectionTypeProviderProps> = ({ children }) => {
  const { collections, loading } = useGetImportedCollections();
  const { clientConfig, loading: clientConfigLoading } = useGetClientConfig();
  const { updateImportSyncSettings } = useUpdateImportSyncSettings();

  // Function to update import source in backend
  const updateImportSourceInBackend = useCallback(async (source: Source) => {
    try {
      // Create update payload with only the fields we want to update
      const importSyncSettingsUpdate: Partial<ImportSyncSettingsInput> = {
        importSource: source,
      };

      // If importSyncSettings exists, preserve all existing values
      if (clientConfig?.importSyncSettings) {
        const existing = clientConfig.importSyncSettings;

        // Preserve existing values by including them in the update
        if (existing.autoSyncFromShopify !== null && existing.autoSyncFromShopify !== undefined) {
          importSyncSettingsUpdate.autoSyncFromShopify = existing.autoSyncFromShopify;
        }

        if (existing.productsImportLimit !== null && existing.productsImportLimit !== undefined) {
          importSyncSettingsUpdate.productsImportLimit = existing.productsImportLimit;
        }
      }

      await updateImportSyncSettings({
        variables: {
          importSyncSettings: importSyncSettingsUpdate
        }
      });
    } catch (error) {
      // TODO: remove this later
      console.error('Failed to update importSource in backend:', error);
    }
  }, [updateImportSyncSettings, clientConfig]);

  const importedCollectionType = useMemo<ImportedCollectionType>(() => {
    // If still loading, return null
    if (loading || clientConfigLoading) {
      return null;
    }

    // FIRST: Check if importSource is already set in API response
    const existingImportSource = clientConfig?.importSyncSettings?.importSource;
    if (existingImportSource !== null && existingImportSource !== undefined) {
      // Return the existing value from API
      return existingImportSource === Source.CSV ? 'CSV' : 'SHOPIFY';
    }

    // SECOND: If importSource is null, check collections data
    if (!collections || collections.length === 0) {
      return null;
    }

    // Check if any collection has CSV source
    const hasCsvCollection = collections.some((collection) =>
      collection.collection?.source === Source.CSV
    );

    // Check if any collection has SHOPIFY source
    const hasShopifyCollection = collections.some((collection) =>
      collection.collection?.source === Source.SHOPIFY
    );

    // Priority: If both exist, we could return based on business logic
    // For now, let's prioritize CSV if it exists, otherwise SHOPIFY
    if (hasCsvCollection) {
      return 'CSV';
    }

    if (hasShopifyCollection) {
      return 'SHOPIFY';
    }

    // If no collections match known sources, return null
    return null;
  }, [collections, loading, clientConfigLoading, clientConfig?.importSyncSettings?.importSource]);

  // Effect to check and update importSource in backend when it's null/undefined
  useEffect(() => {
    // Don't run if still loading client config or collections
    if (loading || clientConfigLoading) {
      return;
    }

    // Check if importSource is null or undefined
    const currentImportSource = clientConfig?.importSyncSettings?.importSource;

    if (currentImportSource == null && importedCollectionType !== null) {
      // We have collections but no importSource set in backend
      const sourceToUpdate = importedCollectionType === 'CSV' ? Source.CSV : Source.SHOPIFY;
      console.log(`ImportSource is ${currentImportSource}, updating to ${sourceToUpdate} based on collections`);
      updateImportSourceInBackend(sourceToUpdate);
    }
  }, [
    loading,
    clientConfigLoading,
    clientConfig?.importSyncSettings?.importSource,
    importedCollectionType,
    updateImportSourceInBackend
  ]);

  const value = useMemo(() => ({
    importedCollectionType,
    loading: loading || clientConfigLoading,
  }), [importedCollectionType, loading, clientConfigLoading]);

  return (
    <ImportedCollectionTypeContext.Provider value={value}>
      {children}
    </ImportedCollectionTypeContext.Provider>
  );
};

export const useImportedCollectionType = (): ImportedCollectionTypeContextValue => {
  const context = useContext(ImportedCollectionTypeContext);
  if (context === undefined) {
    throw new Error('useImportedCollectionType must be used within a ImportedCollectionTypeProvider');
  }
  return context;
};
